import type { DailyEntryResponse } from "@consistency-circle/shared";

export type DayScoreTier = "optimal" | "good" | "fair" | "attention";

export type DayScoreFactor = {
  readonly key: string;
  readonly label: string;
  /** 0-100, or null when nothing was logged for this factor yet. */
  readonly score: number | null;
  readonly weight: number;
  readonly detail: string;
};

export type DayScore = {
  /** 0-100 weighted blend of the factors with data, or null when nothing is logged. */
  readonly score: number | null;
  readonly tier: DayScoreTier | null;
  readonly factors: DayScoreFactor[];
};

/** Map a 1-5 rating onto 0-100. */
const ratingToScore = (rating: number): number => ((rating - 1) / 4) * 100;

const mean = (values: number[]): number =>
  values.reduce((sum, value) => sum + value, 0) / values.length;

export function dayScoreTier(score: number): DayScoreTier {
  if (score >= 80) return "optimal";
  if (score >= 60) return "good";
  if (score >= 40) return "fair";
  return "attention";
}

export const tierLabels: Record<DayScoreTier, string> = {
  optimal: "Optimal",
  good: "Good",
  fair: "Fair",
  attention: "Pay attention",
};

/**
 * Composite day score in the spirit of a readiness score: one number that
 * blends goal completion, mood, sleep, and lifestyle (diet + exercise).
 * Every factor maps to something the check-in actually collects; factors
 * without data are left out of the blend instead of dragging it down.
 */
export function computeDayScore(
  goalCompletionPercent: number | null,
  entry: DailyEntryResponse | null,
): DayScore {
  const mood = entry?.mood ?? null;

  const sleepRatings = entry
    ? [
        entry.sleepQuality === null ? null : ratingToScore(entry.sleepQuality),
        entry.sleepHours === null ? null : sleepHoursScore(entry.sleepHours),
      ].filter((score): score is number => score !== null)
    : [];

  const lifestyleRatings = entry
    ? [entry.dietQuality, entry.exerciseQuality].filter(
        (rating): rating is number => rating !== null,
      )
    : [];

  const factors: DayScoreFactor[] = [
    {
      key: "goals",
      label: "Goals",
      score: goalCompletionPercent,
      weight: 40,
      detail:
        goalCompletionPercent === null
          ? "No goals tracked yet"
          : `${goalCompletionPercent}% of today's actions`,
    },
    {
      key: "mood",
      label: "Mood",
      score: mood === null ? null : ratingToScore(mood),
      weight: 20,
      detail: mood === null ? "Check in to count this" : `Self-rated ${mood} of 5`,
    },
    {
      key: "sleep",
      label: "Sleep",
      score: sleepRatings.length > 0 ? mean(sleepRatings) : null,
      weight: 20,
      detail:
        entry?.sleepHours != null
          ? `${entry.sleepHours}h${entry.sleepQuality != null ? ` · quality ${entry.sleepQuality}/5` : ""}`
          : entry?.sleepQuality != null
            ? `Quality ${entry.sleepQuality}/5`
            : "Log sleep to count this",
    },
    {
      key: "lifestyle",
      label: "Lifestyle",
      score: lifestyleRatings.length > 0 ? mean(lifestyleRatings.map(ratingToScore)) : null,
      weight: 20,
      detail:
        lifestyleRatings.length > 0
          ? [
              entry?.dietQuality != null ? `diet ${entry.dietQuality}/5` : null,
              entry?.exerciseQuality != null ? `exercise ${entry.exerciseQuality}/5` : null,
            ]
              .filter((part): part is string => part !== null)
              .join(" · ")
          : "Rate diet and exercise to count this",
    },
  ];

  const present = factors.filter((factor) => factor.score !== null);
  if (present.length === 0) {
    return { score: null, tier: null, factors };
  }

  const totalWeight = present.reduce((sum, factor) => sum + factor.weight, 0);
  const score = Math.round(
    present.reduce((sum, factor) => sum + (factor.score ?? 0) * factor.weight, 0) / totalWeight,
  );

  return { score, tier: dayScoreTier(score), factors };
}

/** 7-9 hours scores full marks, falling off linearly toward 4h and 12h. */
function sleepHoursScore(hours: number): number {
  if (hours >= 7 && hours <= 9) return 100;
  if (hours < 7) return Math.max(0, ((hours - 4) / 3) * 100);
  return Math.max(0, ((12 - hours) / 3) * 100);
}
